/**
 * Header slot badge showing Internal/External status with a toggle dropdown.
 * Runs in its own ReactDOM.createRoot — no MUI/ThemeProvider. Styles: pdf-review.css
 */

import React, {useEffect, useRef, useState, useSyncExternalStore} from 'react';
import PropTypes from 'prop-types';

import {
  commentVisibilityStore,
  persistAnnotationVisibility,
} from '../store/commentVisibilityStore';
import {LockIcon, GlobeIcon, ChevronDownIcon} from './commentIcons';

const Lock = () => <LockIcon className="cvb-icon" />;
const Globe = () => <GlobeIcon className="cvb-icon" />;
const Chevron = () => <ChevronDownIcon className="cvb-chevron" />;

const CommentVisibilityBadge = ({annotationId, instance}) => {
  const {visibilityMap, postedAnnotationIds} = useSyncExternalStore(
    commentVisibilityStore.subscribe,
    commentVisibilityStore.getSnapshot
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside.
  // Uses composedPath() so the check works inside Nutrient's shadow DOM
  // (where e.target is retargeted to the shadow host on boundary crossing).
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e) => {
      const path = e.composedPath();
      const isInsideDropdown =
        dropdownRef.current && path.includes(dropdownRef.current);
      const isInsideButton =
        buttonRef.current && path.includes(buttonRef.current);
      if (!isInsideDropdown && !isInsideButton) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen]);

  const isPosted = postedAnnotationIds?.has(annotationId) ?? false;
  const isInternal = visibilityMap.get(annotationId) ?? true;

  // This hides the badge while the user is composing the first comment.
  if (!isPosted) return null;

  const handleToggleDropdown = () => {
    if (!isUpdating) setIsOpen((prev) => !prev);
  };

  const handleChangeVisibility = async (newIsInternal) => {
    if (isUpdating || newIsInternal === isInternal) return;
    setIsOpen(false);
    setIsUpdating(true);
    try {
      await persistAnnotationVisibility(instance, annotationId, newIsInternal);
    } catch {
      // handled in persistAnnotationVisibility
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="cvb-container">
      <button
        ref={buttonRef}
        className={`cvb-badge${isInternal ? ' cvb-badge--internal' : ' cvb-badge--external'}${isUpdating ? ' cvb-badge--updating' : ''}`}
        onClick={handleToggleDropdown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={isUpdating}
      >
        {isInternal ? <Lock /> : <Globe />}
        <span className="cvb-badge-label">
          {isInternal ? 'Internal' : 'External'}
        </span>
        <Chevron />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="cvb-dropdown"
          role="menu"
          aria-label="Change visibility"
        >
          {isInternal ? (
            <button
              className="cvb-dropdown-item"
              onClick={() => handleChangeVisibility(false)}
              role="menuitem"
            >
              <Globe />
              <span className="cvb-dropdown-item-text">
                <span className="cvb-dropdown-item-title">Make External</span>
                <span className="cvb-dropdown-item-desc">
                  Include this thread in exports
                </span>
              </span>
            </button>
          ) : (
            <button
              className="cvb-dropdown-item"
              onClick={() => handleChangeVisibility(true)}
              role="menuitem"
            >
              <Lock />
              <span className="cvb-dropdown-item-text">
                <span className="cvb-dropdown-item-title">Make Internal</span>
                <span className="cvb-dropdown-item-desc">
                  Exclude this thread from exports
                </span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

CommentVisibilityBadge.propTypes = {
  annotationId: PropTypes.string.isRequired,
  instance: PropTypes.object,
};

export default CommentVisibilityBadge;
